import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FaGlobeAmericas } from 'react-icons/fa';
import { useAppTranslation } from '../../hooks/useAppTranslation';
import { Menu, MenuItem, SwitchButton, Wrapper } from './styles';

const orderedLanguages: Array<'pt' | 'en' | 'es'> = ['pt', 'en', 'es'];

const LanguageSwitch: React.FC = () => {
  const { i18n, changeLanguage } = useAppTranslation();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  const current = useMemo(() => {
    const lang = (i18n.language || 'pt').toLowerCase();
    if (lang.startsWith('pt')) return 'pt' as const;
    if (lang.startsWith('en')) return 'en' as const;
    if (lang.startsWith('es')) return 'es' as const;
    return 'pt' as const;
  }, [i18n.language]);

  const nextLang = useMemo(() => {
    const idx = orderedLanguages.indexOf(current);
    const next = orderedLanguages[(idx + 1) % orderedLanguages.length];
    return next;
  }, [current]);

  const handleToggleOpen = useCallback(() => {
    setOpen(prev => !prev);
  }, []);

  const handleSelect = useCallback(
    (lang: 'pt' | 'en' | 'es') => {
      changeLanguage(lang);
      setOpen(false);
    },
    [changeLanguage],
  );

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        open &&
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  return (
    <Wrapper className="language-switch">
      <SwitchButton
        ref={buttonRef}
        type="button"
        onClick={handleToggleOpen}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={i18n.t('components.languageSwitch.label')}
        title={i18n.t('components.languageSwitch.label')}
      >
        <FaGlobeAmericas size={16} />
      </SwitchButton>
      {open && (
        <Menu
          ref={menuRef}
          role="menu"
          aria-label={i18n.t('components.languageSwitch.label')}
        >
          <MenuItem
            role="menuitemradio"
            aria-checked={current === 'pt'}
            onClick={() => handleSelect('pt')}
            tabIndex={0}
          >
            {i18n.t('components.languageSwitch.pt')}
          </MenuItem>
          <MenuItem
            role="menuitemradio"
            aria-checked={current === 'en'}
            onClick={() => handleSelect('en')}
            tabIndex={0}
          >
            {i18n.t('components.languageSwitch.en')}
          </MenuItem>
          <MenuItem
            role="menuitemradio"
            aria-checked={current === 'es'}
            onClick={() => handleSelect('es')}
            tabIndex={0}
          >
            {i18n.t('components.languageSwitch.es')}
          </MenuItem>
        </Menu>
      )}
    </Wrapper>
  );
};

export default LanguageSwitch;
